import { BaseItem } from './common';

interface BaseServiceAccount {
  token: string;
  description?: string;
  deleted: boolean;
  deletedTimestamp: Date | null;
}

export interface CreateValues
  extends Pick<BaseServiceAccount, 'token' | 'description'> {}
export interface UpdateValues extends Partial<BaseServiceAccount> {}

export interface ServiceAccount extends BaseServiceAccount, BaseItem {}
export interface ServiceAccountWithScopes extends ServiceAccount {
  scopes: ServiceAccountScope[];
}
export interface CreatedServiceAccount extends ServiceAccount {
  secret: string;
}

export enum Scope {
  CalendarSync = 'CalendarSync',
  CalendarSyncCallback = 'CalendarSyncCallback',
  CheckAlerts = 'CheckAlerts',
  CreatePrevisitFormSet = 'CreatePrevisitFormSet',
  DeleteRecordings = 'DeleteRecordings',
  DocumoWebhook = 'DocumoWebhook',
  EhrDeploymentValidation = 'EhrDeploymentValidation',
  EncounterCreation = 'EncounterCreation',
  EncounterDeletion = 'EncounterDeletion',
  EncounterProcessPayments = 'EncounterProcessPayments',
  EncounterProcessPayouts = 'EncounterProcessPayouts',
  ExportAllergiesCallback = 'ExportAllergiesCallback',
  ExportAppointmentNoteCallback = 'ExportAppointmentNoteCallback',
  ExportAppointmentStatusCallback = 'ExportAppointmentStatusCallback',
  ExportAuthorizationFormCallback = 'ExportAuthorizationFormCallback',
  ExportAuthorizationStatusCallback = 'ExportAuthorizationStatusCallback',
  ExportAuthorizationSubmissionCallback = 'ExportAuthorizationSubmissionCallback',
  ExportBillingCallback = 'ExportBillingCallback',
  ExportBillingGroupCallback = 'ExportBillingGroupCallback',
  ExportCareGapFindingsCallback = 'ExportCareGapFindingsCallback',
  ExportClinicalCommentCallback = 'ExportClinicalCommentCallback',
  ExportEncounterInformationCallback = 'ExportEncounterInformationCallback',
  ExportEncounterPatientData = 'ExportEncounterPatientData',
  ExportFamilyMedicalHistoryCallback = 'ExportFamilyMedicalHistoryCallback',
  ExportFindingEvidenceCallback = 'ExportFindingEvidenceCallback',
  ExportFingerprintedNoteCallback = 'ExportFingerprintedNoteCallback',
  ExportFollowUpCallback = 'ExportFollowUpCallback',
  ExportFormOutcomesCallback = 'ExportFormOutcomesCallback',
  ExportFormsCallback = 'ExportFormsCallback',
  ExportGeneralCommentCallback = 'ExportGeneralCommentCallback',
  ExportGuarantorsCallback = 'ExportGuarantorsCallback',
  ExportHccFindings = 'ExportHccFindingsCallback',
  ExportMarkAsReviewedCallback = 'ExportMarkAsReviewedCallback',
  ExportMedicationsCallback = 'ExportMedicationsCallback',
  ExportNoteCallback = 'ExportNoteCallback',
  ExportNoteCheckedInCallback = 'ExportNoteCheckedInCallback',
  ExportOrdersCallback = 'ExportOrdersCallback',
  ExportPatientCareProvidersCallback = 'ExportPatientCareProvidersCallback',
  ExportPatientInformationCallback = 'ExportPatientInformationCallback',
  ExportPatientInsuranceInformationCallback = 'ExportPatientInsuranceInformationCallback',
  ExportPatientMedicalHistoryCallback = 'ExportPatientMedicalHistoryCallback',
  ExportPatientOutreachesCallback = 'ExportPatientOutreachesCallback',
  ExportPatientPharmaciesCallback = 'ExportPatientPharmaciesCallback',
  ExportPatientUploadStatusesCallback = 'ExportPatientUploadStatusesCallback',
  ExportPatientUploadsCallback = 'ExportPatientUploadsCallback',
  ExportPrenatalCallback = 'ExportPrenatalCallback',
  ExportPreregistrationEncounter = 'ExportPreregistrationEncounterCallback',
  ExportPrevisitNoteCallback = 'ExportPrevisitNoteCallback',
  ExportProcessedPaymentsCallback = 'ExportProcessedPaymentsCallback',
  ExportQualityMeasuresCallback = 'ExportQualityMeasuresCallback',
  ExportRegistrationNote = 'ExportRegistrationNote',
  ExportReviewClinicalAdvisories = 'ExportReviewClinicalAdvisories',
  ExportScheduledAppointmentCallback = 'ExportScheduledAppointmentCallback',
  ExportScheduledOrderAssociationCallback = 'ExportScheduledOrderAssociationCallback',
  ExportSocialHistoryCallback = 'ExportSocialHistoryCallback',
  ExportSurgicalHistoryCallback = 'ExportSurgicalHistoryCallback',
  ExportTerminatedInsurancesCallback = 'ExportTerminatedInsurancesCallback',
  ExportVerifyRegistrationCallback = 'ExportVerifyRegistrationCallback',
  ExtractAllergiesCallback = 'ExtractAllergiesCallback',
  ExtractAuthorizationStatusCallback = 'ExtractAuthorizationStatusCallback',
  ExtractBillingCallback = 'ExtractBillingCallback',
  ExtractCareGapFindingsCallback = 'ExtractCareGapFindingsCallback',
  ExtractCareGapStatusesCallback = 'ExtractCareGapStatusesCallback',
  ExtractCarryCurrentCallback = 'ExtractCarryCurrentCallback',
  ExtractCarryForward = 'ExtractCarryForward',
  ExtractEncounterInformationCallback = 'ExtractEncounterInformationCallback',
  ExtractEncounterPatientData = 'ExtractEncounterPatientData',
  ExtractFamilyMedicalHistoryCallback = 'ExtractFamilyMedicalHistoryCallback',
  ExtractGuarantorsCallback = 'ExtractGuarantorsCallback',
  ExtractHistoricalEncountersIngestCallback = 'ExtractHistoricalEncountersIngestCallback',
  ExtractHistoricalPatientEncountersCallback = 'ExtractHistoricalPatientEncountersCallback',
  ExtractImmunizationRegistry = 'ExtractImmunizationRegistry',
  ExtractLabValuesCallback = 'ExtractLabValuesCallback',
  ExtractMedicationsCallback = 'ExtractMedicationsCallback',
  ExtractMspqCallback = 'ExtractMspqCallback',
  ExtractOutstandingBalancesCallback = 'ExtractOutstandingBalancesCallback',
  ExtractPatientAdvisoriesCallback = 'ExtractPatientAdvisoriesCallback',
  ExtractPatientAdvisoryDocumentsCallback = 'ExtractPatientAdvisoryDocumentsCallback',
  ExtractPatientCareProvidersCallback = 'ExtractPatientCareProvidersCallback',
  ExtractPatientCommunicationConsentCallback = 'ExtractPatientCommunicationConsentCallback',
  ExtractPatientDocumentsCallback = 'ExtractPatientDocumentsCallback',
  ExtractPatientInformationCallback = 'ExtractPatientInformationCallback',
  ExtractPatientInsuranceCallback = 'ExtractPatientInsuranceCallback',
  ExtractPatientInsuranceRteInformationCallback = 'ExtractPatientInsuranceRteInformationCallback',
  ExtractPatientMedicalHistoryCallback = 'ExtractPatientMedicalHistoryCallback',
  ExtractPatientOrderCallback = 'ExtractPatientOrderCallback',
  ExtractPatientPharmaciesCallback = 'ExtractPatientPharmaciesCallback',
  ExtractPatientProblemsCallback = 'ExtractPatientProblemsCallback',
  ExtractPatientUploadsCallback = 'ExtractPatientUploadsCallback',
  ExtractPredictBillingDataCallback = 'ExtractPredictBillingDataCallback',
  ExtractPregnancyCallback = 'ExtractPregnancyCallback',
  ExtractProblemGroupsCallback = 'ExtractProblemGroupsCallback',
  ExtractQualityMeasuresCallback = 'ExtractQualityMeasuresCallback',
  ExtractSocialHistoryCallback = 'ExtractSocialHistoryCallback',
  ExtractSurgicalHistoryCallback = 'ExtractSurgicalHistoryCallback',
  ExtractUpcomingEncounterPatientData = 'ExtractUpcomingEncounterPatientData',
  ExtractVitalsCallback = 'ExtractVitalsCallback',
  FetchInterpretationsCallback = 'FetchInterpretationsCallback',
  FindClosedEncounters = 'FindClosedEncounters',
  GetEncountersDigest = 'GetEncountersDigest',
  GetNoteMacros = 'GetNoteMacros',
  GetNoteTemplates = 'GetNoteTemplates',
  GetPaymentReport = 'GetPaymentReport',
  GetPayouts = 'GetPayouts',
  HL7CalendarSyncCallback = 'HL7CalendarSyncCallback',
  HistoricalIngestionCallback = 'HistoricalIngestionCallback',
  LabelBoxWebhook = 'LabelBoxWebHook',
  NoteCheckFetchingStatus = 'NoteCheckFetchingStatus',
  NoticeOfAdmissionSync = 'NoticeOfAdmissionSync',
  OutreachSync = 'OutreachSync',
  OutreachSyncCallback = 'OutreachSyncCallback',
  PatientListSync = 'PatientListSync',
  PatientListSyncCallback = 'PatientListSyncCallback',
  PatientOrdersSync = 'PatientOrdersSync',
  PatientOrdersSyncCallback = 'PatientOrdersSyncCallback',
  PatientSSN = 'PatientSSN',
  PayoutPractices = 'PayoutPractices',
  PrevisitExportUpdatedFormSets = 'PrevisitExportUpdatedFormSets',
  PrevisitPatientNotifications = 'PrevisitPatientNotifications',
  PrevisitReportSync = 'PrevisitReportSync',
  ProcessEhrTimeSlots = 'ProcessEhrTimeSlots',
  ProcessScheduledNotifications = 'ProcessScheduledNotifications',
  ProcessScheduledWorks = 'ProcessScheduledWorks',
  ProcessSsnAndKeys = 'ProcessSsnAndKeys',
  ReckonClosedEncounters = 'ReckonClosedEncounters',
  ReckonClosedEncountersCallback = 'ReckonClosedEncountersCallback',
  ReckonJobCallback = 'ReckonJobCallback',
  RequestRefundsViaSftp = 'RequestRefundsViaSftp',
  ResetRateLimiter = 'ResetRateLimiter',
  RpaJobEventCallback = 'RpaJobEventCallback',
  SyncFeeds = 'SyncFeeds',
  TranscribeRecordingCallback = 'TranscribeRecordingCallback',
  ValueBasedCare = 'ValueBasedCare',
}

export interface BaseServiceAccountScope {
  serviceAccountId: string;
  scope: Scope;
}
export interface ServiceAccountScope
  extends BaseServiceAccountScope,
    BaseItem {}
